import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '@/lib/utils.ts';

const buttonVariants = tv({
  base: cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap uppercase text-sm font-bold ring-offset-background transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative'
  ),
  variants: {
    variant: {
      default: 'bg-primary hover:bg-primary-hover text-primary-foreground',
      accent: 'bg-accent hover:bg-accent-hover text-primary-foreground',
      secondary:
        'bg-secondary hover:bg-secondary-hover text-secondary-foreground',
      approve: 'bg-success hover:bg-success-hover text-success-foreground',
      destructive:
        'bg-destructive hover:bg-destructive-hover text-destructive-foreground',
      text: 'text-foreground',
      muted: '',
    },
    shape: {
      default: 'rounded-md font-normal',
      square: 'rounded-sm font-normal',
      rounded: 'rounded-full',
    },
    size: {
      sm: 'h-[30px] px-3',
      default: 'h-10 px-3 py-2 text-sm',
      lg: 'h-[54px] px-8 text-lg rounded-[16px]',
      icon: 'h-10 w-10',
    },
  },
  compoundVariants: [
    {
      shape: 'default',
      size: 'lg',
      className: 'rounded-lg',
    },
  ],
  defaultVariants: {
    shape: 'default',
    variant: 'default',
    size: 'default',
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, shape, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
